
import { Loader2 } from "lucide-react";
import { UserRoleTable } from "./UserRoleTable";
import { ErrorState } from "@/components/ui/error-state";
import { UserSearch } from "./UserSearch";
import { useUsers, SortField } from "./hooks/useUsers";
import { CustomPagination } from "@/components/CustomPagination";

export function UserRoleManagement() {
  const {
    filteredUsers, 
    loading, 
    error, 
    searchQuery, 
    setSearchQuery,
    currentPage,
    totalPages,
    setCurrentPage,
    sortOrder,
    sortField,
    setSortBy,
    roleFilter,
    setRoleFilter,
    showMultiRoleOnly,
    setShowMultiRoleOnly
  } = useUsers();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <ErrorState title="Error Loading Users" message={error} />;
  }

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button 
      onClick={() => setSortBy(field)}
      className={`flex items-center text-sm py-2 px-4 border rounded-md transition-colors ${
        sortField === field 
          ? 'bg-primary text-primary-foreground border-primary' 
          : 'hover:bg-accent border-border'
      }`}
    >
      {label}: {sortField === field ? (sortOrder === 'asc' ? '▲' : '▼') : '–'}
    </button>
  );

  return (
    <>
      <h2 className="text-xl font-semibold mb-4">User Role Management</h2>
      
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <UserSearch 
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, email, or role..."
          />
          
          <div className="flex gap-2">
            <SortButton field="usage" label="Usage" />
            <SortButton field="email" label="Email" />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">Filter by role:</span>
          {['admin', 'specialist', 'caregiver'].map(role => (
            <button
              key={role}
              onClick={() => {
                setRoleFilter(prev => 
                  prev.includes(role) 
                    ? prev.filter(r => r !== role)
                    : [...prev, role]
                );
              }}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                roleFilter.includes(role)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-accent border-border'
              }`}
            >
              {role}
            </button>
          ))}
          <button
            onClick={() => setShowMultiRoleOnly(!showMultiRoleOnly)}
            className={`px-3 py-1 text-sm rounded-md border transition-colors ${
              showMultiRoleOnly
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-accent border-border'
            }`}
          >
            2+ Roles
          </button>
          {(roleFilter.length > 0 || showMultiRoleOnly) && (
            <button
              onClick={() => {
                setRoleFilter([]);
                setShowMultiRoleOnly(false);
              }}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
      
      {filteredUsers.length > 0 ? (
        <>
          <UserRoleTable users={filteredUsers} />
          
          <div className="mt-6 flex justify-center">
            <CustomPagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery ? "No users found matching your search" : "No users found"}
        </div>
      )}
    </>
  );
}
